import { isValid } from 'date-fns'
import { standardizeString } from '@skop-app/lib-domain/utils'

const NUMERIC_STRING_PATTERN = /^[+-]?(?:[\d\s.,]+)(?:[eE][+-]?\d+)?$/

const normalizeNumericMantissa = (value: string): string => {
  let normalizedValue = value

  if (normalizedValue.includes(',') && normalizedValue.includes('.')) {
    const lastComma = normalizedValue.lastIndexOf(',')
    const lastDot = normalizedValue.lastIndexOf('.')

    if (lastComma > lastDot) {
      normalizedValue = normalizedValue.replace(/\./g, '').replace(',', '.')
    } else {
      normalizedValue = normalizedValue.replace(/,/g, '')
    }
  } else {
    normalizedValue = normalizedValue.replace(/,/g, '.')
  }

  return normalizedValue
}

export const isSupportedNumericString = (value: unknown): value is string => {
  if (typeof value !== 'string') return false

  const trimmedValue = value.trim()
  return trimmedValue.length > 0 && NUMERIC_STRING_PATTERN.test(trimmedValue)
}

/**
 * Parses a value into a normalized boolean.
 * Handles: 'true', '1', 'oui', 'yes' (case insensitive) -> true
 * Handles: 'false', '0', 'non', 'no' (case insensitive) -> false
 */
export const parseBoolean = (value: any): boolean | undefined => {
  if (value === undefined || value === null) return undefined
  const normalizedValue = standardizeString(String(value))

  if (['true', '1', 'oui', 'yes'].includes(normalizedValue)) {
    return true
  }
  if (['false', '0', 'non', 'no'].includes(normalizedValue)) {
    return false
  }
  return undefined
}

/**
 * Parses a value into a number.
 * Handles: string numbers with spaces, currency symbols, commas/dots.
 */
export const parseNumber = (value: any): number | null => {
  if (value === undefined || value === null || value === '') return null
  if (typeof value === 'number') return value

  const stringValue = String(value)
  const scientificCandidate = stringValue
    .replace(/[^0-9eE,+.\-\s]/g, '')
    .replace(/\s+/g, '')
  const scientificMatch = scientificCandidate.match(
    /^([+-]?)([\d.,]+)([eE][+-]?\d+)$/,
  )

  if (scientificMatch) {
    const [, sign, mantissa, exponent] = scientificMatch
    const parsedScientific = Number(
      `${sign}${normalizeNumericMantissa(mantissa)}${exponent}`,
    )

    if (!Number.isNaN(parsedScientific)) return parsedScientific
  }

  // Remove all non-numeric characters except , . -
  let cleanedValue = stringValue.replace(/[^0-9,.-]/g, '').replace(/\s+/g, '')

  cleanedValue = normalizeNumericMantissa(cleanedValue)

  const parsed = parseFloat(cleanedValue)
  return isNaN(parsed) ? null : parsed
}

/**
 * Parses a value into a Date object.
 * Handles:
 * - JS Date objects
 * - String dates (ISO, DD/MM/YYYY)
 * - Excel numeric dates (days since 1900)
 */
export const parseDate = (value: any): Date | null => {
  if (value === undefined || value === null || value === '') return null
  if (typeof value === 'number' && value <= 0) return null

  // 1. Already a Date
  if (value instanceof Date) {
    return isValid(value) ? value : null
  }

  // 2. String handling
  if (typeof value === 'string') {
    const trimmedValue = value.trim()
    if (!trimmedValue) return null
    if (/^[+-]?0+(?:[.,]0+)?$/.test(trimmedValue)) return null

    // DD/MM/YYYY format
    if (trimmedValue.includes('/') && !trimmedValue.includes('GMT')) {
      const [day, month, year] = trimmedValue
        .split('/')
        .map((num) => parseInt(num, 10))
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        // Month is 0-indexed
        const dateFromString = new Date(year, month - 1, day)
        return isValid(dateFromString) ? dateFromString : null
      }
    }

    // ISO or GMT string
    const date = new Date(trimmedValue)
    return isValid(date) ? date : null
  }

  // 3. Excel Numeric Date handling
  const numericValue = parseFloat(value)
  if (!isNaN(numericValue)) {
    // Excel stores dates as number of days since 1/1/1900
    // Adjust for Excel leap year bug (1900 is not a leap year in Gregorian but is in Excel)
    // and 25569 offset for Unix epoch

    // Note: DataFormating used 25569. EditReference used 25567 + 1 (25568).
    // Standard offset is 25569.

    const utcDays = Math.floor(numericValue - 25569)
    const utcValue = utcDays * 86400000
    const dateFromExcel = new Date(utcValue)

    return isValid(dateFromExcel) ? dateFromExcel : null
  }

  return null
}

/**
 * Cleans a string value by removing special characters and extra spaces.
 */
export const cleanStringValue = (value: any): string | null => {
  if (value === undefined || value === null) return null

  return String(value)
    .replace(
      /[\r\n\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/g,
      ' ',
    )
    .trim()
}

/**
 * Splits a comma-separated string into an array of trimmed strings.
 */
export const parseStringList = (
  value: any,
  separators: RegExp = /[,/]/,
): string[] => {
  if (value === undefined || value === null) return []
  if (Array.isArray(value)) return value.map(String)

  return String(value)
    .split(separators)
    .map((v) => v.trim())
    .filter(Boolean)
}
